import { ApiClient } from "@/services/apiClient";
import { AxiosError } from "axios";
import { type CollectionItemId } from "@/services/collection/item/collectionItemTypes";
import { type PublicCollectionItem, type PublicCollectionItem_FromCollectible, PublicItemNotFoundException } from "@/services/collection/item/public/publicCollectionItemTypes";
import type { OnlyPublicProperties } from "@/types";
import { PublicCollectionItemsFilter } from "@/services/collection/item/public";
import { z } from "zod";
import { WholeNumber_Schema } from "@/utilities";
import { PublicCollectionItem_FromCollectible_Schema, PublicCollectionItem_ListItem_Schema } from "@/services/collection/item/public/publicCollectionItemSchema";
import { type CollectibleId } from "@/services/collectibles";

export async function fetchPublicCollectionItemById(itemId: CollectionItemId): Promise<PublicCollectionItem> {
  const url = `api-public/user/collector-item`;
  const params = { id: itemId };
  return ApiClient.getPublic<PublicCollectionItem>(url, params)
    .then(({ data }) => data)
    .catch((error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        throw new PublicItemNotFoundException();
      } else {
        throw error;
      }
    });
}

/**
 * =============================================================================
 * List of items (user profile)
 * =============================================================================
 */
const ApiResponse_ItemList_Schema = z.object({
  count: WholeNumber_Schema,
  result: PublicCollectionItem_ListItem_Schema.array(),
});
type ApiResponse_ItemList = z.infer<typeof ApiResponse_ItemList_Schema>;

export const fetchPublicCollectionItemsForUser = async (filter: OnlyPublicProperties<PublicCollectionItemsFilter.Filter>): Promise<ApiResponse_ItemList> => {
  const url = `api-public/user/collection-listed-item`;
  const params = PublicCollectionItemsFilter.createApiParams(filter);
  return ApiClient.getPublic<ApiResponse_ItemList>(url, params).then(({ data }) => ApiResponse_ItemList_Schema.parse(data));
  // Intentionally no catch() ... Errors should be handled directly in items grid/list component.
};

/**
 * =========================================================================
 * List of Collection items based on given Collectible.
 * =========================================================================
 */

const ApiResponse_PublicCollectionItemFromCollectibleList_Schema = z
  .object({
    collection: PublicCollectionItem_FromCollectible_Schema.array(),
    totalCount: WholeNumber_Schema,
  })
  .strict();

type ApiResponse_CollectionItemFromCollectible_List = z.infer<typeof ApiResponse_PublicCollectionItemFromCollectibleList_Schema>;

export const fetchForCollectible = async (id: CollectibleId): Promise<PublicCollectionItem_FromCollectible[]> => {
  const url = "/api-public/collection/in-collection";
  const query = {
    collectible: id,
    page: 0,
    perPage: 9000, // 'perPage' is required, but since the total number of items is unknown, we load the first x items.
  };
  return ApiClient.getPublic<ApiResponse_CollectionItemFromCollectible_List>(url, query).then(({ data }) => ApiResponse_PublicCollectionItemFromCollectibleList_Schema.parse(data).collection);
};
